import { Tool, type ToolContext } from './base/Tool';
import { SelectTool } from './SelectTool';
import { RectangleTool } from './RectangleTool';
import { EllipseTool } from './EllipseTool';
import { DiamondTool } from './DiamondTool';
import { ArrowTool } from './ArrowTool';
import { LineTool } from './LineTool';
import { TextTool } from './TextTool';
import { IconTool } from './IconTool';
import { SvgTool } from './SvgTool';
import type { ToolType, Element } from '@/types';
import type { RootStore } from '@/stores/RootStore';

export class ToolManager {
  private tools: Map<ToolType, Tool>;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.tools = new Map();
    this.tools.set('select', new SelectTool());
    this.tools.set('rectangle', new RectangleTool());
    this.tools.set('ellipse', new EllipseTool());
    this.tools.set('diamond', new DiamondTool());
    this.tools.set('arrow', new ArrowTool());
    this.tools.set('line', new LineTool());
    this.tools.set('text', new TextTool());
    this.tools.set('icon', new IconTool());
    this.tools.set('svg', new SvgTool());
  }

  /**
   * Set the active tool
   */
  setActiveTool(_toolType: ToolType): void {
    // This is now handled by the UIStore
  }

  /**
   * Get the current active tool
   */
  getActiveTool(): Tool | null {
    const activeToolType = this.rootStore.uiStore.activeTool as ToolType;
    return this.tools.get(activeToolType) || null;
  }

  /**
   * Get tool by type
   */
  getTool(toolType: ToolType): Tool | null {
    return this.tools.get(toolType) || null;
  }

  /**
   * Get all available tools
   */
  getAllTools(): Tool[] {
    return Array.from(this.tools.values());
  }

  /**
   * Handle tool start operation
   */
  onStart(context: Omit<ToolContext, 'rootStore'>): Element | null {
    const activeTool = this.getActiveTool();
    if (!activeTool) return null;
    return activeTool.onStart({ ...context, rootStore: this.rootStore });
  }

  /**
   * Handle tool move operation
   */
  onMove(context: Omit<ToolContext, 'rootStore'>): Element | null {
    const activeTool = this.getActiveTool();
    if (!activeTool) return null;
    const result = activeTool.onMove({ ...context, rootStore: this.rootStore });
    return result || null;
  }

  /**
   * Handle tool end operation
   */
  onEnd(context: Omit<ToolContext, 'rootStore'>): void {
    const activeTool = this.getActiveTool();
    if (!activeTool) return;
    const newElement = activeTool.onEnd({
      ...context,
      rootStore: this.rootStore,
    });

    if (newElement) {
      this.rootStore.projectStore.createElement(newElement);
    }
  }

  /**
   * Handle tool cancel operation
   */
  onCancel(context: Omit<ToolContext, 'rootStore'>): void {
    const activeTool = this.getActiveTool();
    if (!activeTool) return;
    activeTool.onCancel({ ...context, rootStore: this.rootStore });
  }

  /**
   * Get cursor for active tool
   */
  getCursor(context: Omit<ToolContext, 'rootStore'>): string {
    const activeTool = this.getActiveTool();
    if (!activeTool) return 'default';
    return activeTool.getCursor({ ...context, rootStore: this.rootStore });
  }

  onDoubleClick(context: ToolContext) {
    const activeTool = this.getActiveTool();
    if (activeTool?.onDoubleClick) {
      activeTool.onDoubleClick(context);
    }
  }
}
